import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Alert,
  Snackbar,
  IconButton,
  Divider
} from '@mui/material';
import { PaymentElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { getStripeForAccount } from '../context/StripeContext';
import DynamicStripeService from '../services/DynamicStripeService';
import AuthService from '../services/AuthService';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Message, PaymentStatus, normalizePaymentStatus } from '../models/Message';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import { tok } from '../theme/businessTheme';

// Use the shared Stripe instance from StripeContext to avoid multiple initializations

interface PaymentBubbleProps {
  message: Message;
  formId?: string;
  onPaymentComplete?: (message: Message) => void;
}

// Separate component for the payment form that has its own Elements provider
const PaymentForm: React.FC<{
  clientSecret: string;
  amount: number;
  currency: string;
  description?: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
}> = ({ clientSecret, amount, currency, description, onSuccess, onError, isProcessing, setIsProcessing }) => {
  // Access Stripe hooks inside the Elements provider
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError('Payment system not ready');
      return;
    }

    setIsProcessing(true);

    try {
      // First, submit the elements
      const { error: submitError } = await elements.submit();
      
      if (submitError) {
        onError(submitError.message || 'Form validation failed');
        setIsProcessing(false);
        return;
      }

      // Then confirm the payment
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (error) {
        onError(error.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess();
      }
    } catch (error: any) {
      onError(error.message || 'Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{
        backgroundColor: tok('surface'),
        border: `1px solid ${tok('cardBorder')}`,
        p: 2,
        borderRadius: '14px',
        mb: 3,
        textAlign: 'center'
      }}>
        <Typography variant="h4" sx={{ color: tok('onSurface'), mb: 0.5 }}>
          {formatAmount(amount, currency)}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ color: tok('onSurfaceMuted') }}>
            {description}
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 3 }}>
        <PaymentElement />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || isProcessing}
          fullWidth
          sx={{
            py: 1.5,
            fontSize: '1rem',
            borderRadius: '12px',
            background: tok('action'),
            color: tok('onAction'),
            '&:hover': {
              background: tok('action'),
              boxShadow: 'none',
            }
          }}
          startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isProcessing ? 'Processing...' : `Pay ${formatAmount(amount, currency)}`}
        </Button>
      </Box>
    </form>
  );
};

const PaymentBubble: React.FC<PaymentBubbleProps> = ({
  message,
  formId,
  onPaymentComplete
}) => {
  // Live status from the server-owned message doc (the client never writes it).
  const { paymentStatus: liveStatus } = usePaymentStatus(message, formId);
  const status = normalizePaymentStatus(liveStatus);
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const paymentData = message.paymentData;
  const [clientSecret, setClientSecret] = useState<string | undefined>(paymentData?.client_secret || paymentData?.clientSecret);
  const [accountStripePromise, setAccountStripePromise] = useState<ReturnType<typeof getStripeForAccount> | null>(null);
  const [isLoadingStripeAccount, setIsLoadingStripeAccount] = useState(false);


  if (!paymentData) {
    return null;
  }

  const actualClientSecret = paymentData.client_secret || paymentData.clientSecret;
  const actualPaymentIntentId = paymentData.payment_intent_id || paymentData.paymentIntentId;

  if (!actualClientSecret) {
    console.error('Payment message is missing a client secret');
  }

  const getStatusColor = (st: PaymentStatus) => {
    switch (st) {
      case PaymentStatus.PAID:
        return 'success';
      case PaymentStatus.DISPUTED:
      case PaymentStatus.DISPUTE_LOST:
        return 'error';
      case PaymentStatus.CANCELLED:
      case PaymentStatus.REFUNDED:
      case PaymentStatus.PARTIALLY_REFUNDED:
        return 'warning';
      case PaymentStatus.PROCESSING:
      case PaymentStatus.UNKNOWN:
        return 'default';
      default:
        return 'primary';
    }
  };

  const getStatusText = (st: PaymentStatus) => {
    switch (st) {
      case PaymentStatus.PAID:
        return 'Paid';
      case PaymentStatus.PARTIALLY_REFUNDED:
        return 'Partially refunded';
      case PaymentStatus.REFUNDED:
        return 'Refunded';
      case PaymentStatus.CANCELLED:
        return 'Cancelled';
      case PaymentStatus.PROCESSING:
        return 'Processing';
      case PaymentStatus.DISPUTED:
      case PaymentStatus.DISPUTE_LOST:
        return 'Under review';
      case PaymentStatus.UNKNOWN:
        return 'Unknown';
      default:
        return 'Pending';
    }
  };

  const getStatusIcon = (st: PaymentStatus) => {
    switch (st) {
      case PaymentStatus.PAID:
      case PaymentStatus.PARTIALLY_REFUNDED:
        return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
      case PaymentStatus.DISPUTED:
      case PaymentStatus.DISPUTE_LOST:
        return <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const handlePayNow = async () => {
    const existingClientSecret = paymentData?.client_secret || paymentData?.clientSecret;

    if (!existingClientSecret) {
      setPaymentError('Payment not properly initialized by business');
      return;
    }

    try {
      setIsLoadingStripeAccount(true);

      // Use dynamic configuration like mobile apps
      const dynamicStripeService = DynamicStripeService.getInstance();
      const configuredStripe = await dynamicStripeService.configureForPayment(existingClientSecret);

      if (!configuredStripe) {
        throw new Error('Failed to configure Stripe');
      }

      // Set the configured Stripe instance and client secret
      setAccountStripePromise(Promise.resolve(configuredStripe));
      setClientSecret(existingClientSecret);

      setShowPaymentSheet(true);
      setPaymentError(null);

    } catch (error: any) {
      console.error('Payment configuration failed:', error?.message);
      setPaymentError(`Payment configuration failed: ${error.message}`);
    } finally {
      setIsLoadingStripeAccount(false);
    }
  };

  // Note: the client never writes paymentData.status to Firestore. The
  // Stripe webhook (server) is the only writer of 'paid'; the UI reflects it
  // via the usePaymentStatus Firestore listener. The post-success poll below
  // only calls /api/payment-status, which reconciles server-side.

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start', // Always align payment bubbles to the left
        width: '100%',
        mb: 1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '16px',
          borderBottomLeftRadius: '6px',
          maxWidth: 380,
          background: tok('businessBubble'),
          color: tok('onBusinessBubble'),
          position: 'relative',
          boxShadow: '0 8px 24px rgba(18, 41, 31, 0.25)',
          border: '1px solid rgba(245, 240, 232, 0.12)',
        }}
      >
        {/* Status Badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CreditCardIcon sx={{ fontSize: 16, color: tok('onBusinessBubble') }} />
            <Typography
              variant="overline"
              sx={{ color: tok('onBusinessBubble'), fontSize: '0.65rem', lineHeight: 1 }}
            >
              {paymentData.label || 'Payment Request'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(status)}
            <Chip
              label={getStatusText(status)}
              color={getStatusColor(status)}
              size="small"
              sx={{ 
                fontWeight: 'bold',
                fontSize: '0.75rem',
                ...(status === PaymentStatus.PAID && {
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white'
                })
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ backgroundColor: 'rgba(245, 240, 232, 0.15)', mb: 2 }} />

        {/* Payment Details - Extract from messageText dynamically */}
        <Box sx={{ mb: 2.5 }}>
          {(() => {
            try {
              const parsed = JSON.parse(message.messageText);
              const required = parsed.Required || {};
              const optional = parsed.Optional || {};
              const purpose = parsed.Purpose;

              const hasFormData = Object.keys(required).length > 0 || Object.keys(optional).length > 0;

              return (
                <Box>
                  {/* Display Purpose if available */}
                  {purpose && (
                    <Typography variant="body2" sx={{ color: tok('onBusinessBubble'), mb: 1.5, opacity: 0.9 }}>
                      {purpose}
                    </Typography>
                  )}

                  {hasFormData && (
                    <>
                      {/* Display Required Fields */}
                      {Object.keys(required).length > 0 && (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography variant="subtitle2" sx={{ color: tok('onBusinessBubble'), mb: 0.25 }}>
                            Required
                          </Typography>
                          <Box sx={{ ml: 1 }}>
                            {Object.entries(required).map(([key, value], index) => (
                              <Box key={index} sx={{ mt: index > 0 ? 0.75 : 0 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(245, 240, 232, 0.65)', display: 'block' }}>
                                  {key}
                                </Typography>
                                <Typography variant="body2" sx={{ color: tok('onBusinessBubble'), fontWeight: 500 }}>
                                  {String(value)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Display Optional Fields */}
                      {Object.keys(optional).length > 0 && (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography variant="subtitle2" sx={{ color: tok('onBusinessBubble'), mb: 0.25 }}>
                            Optional
                          </Typography>
                          <Box sx={{ ml: 1 }}>
                            {Object.entries(optional).map(([key, value], index) => (
                              <Box key={index} sx={{ mt: index > 0 ? 0.75 : 0 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(245, 240, 232, 0.65)', display: 'block' }}>
                                  {key}
                                </Typography>
                                <Typography variant="body2" sx={{ color: tok('onBusinessBubble'), fontWeight: 500 }}>
                                  {String(value)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                  )}

                  {/* Amount Display */}
                  <Box sx={{
                    mt: 2,
                    px: 1.5,
                    py: 1,
                    backgroundColor: 'rgba(245, 240, 232, 0.08)',
                    border: '1px solid rgba(245, 240, 232, 0.12)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(245, 240, 232, 0.65)' }}>
                      Total due
                    </Typography>
                    <Typography variant="h4" sx={{ color: tok('onBusinessBubble') }}>
                      {formatAmount(paymentData.amount, paymentData.currency)}
                    </Typography>
                  </Box>
                </Box>
              );
            } catch (e) {
              // Fallback to simple display
              return (
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.8rem' }}>
                    {formatAmount(paymentData.amount, paymentData.currency)}
                  </Typography>
                  {paymentData.description && (
                    <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.4 }}>
                      {paymentData.description}
                    </Typography>
                  )}
                </Box>
              );
            }
          })()}
        </Box>

        {/* Pay Now Button - matching iOS style */}
        {status === PaymentStatus.OPEN && (
          <>
            {!actualClientSecret ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                <strong>💳 Payment Error:</strong><br/>
                Payment not properly initialized by business.<br/>
                <small>Missing clientSecret from iOS_Business</small>
              </Alert>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={handlePayNow}
                disabled={isProcessing || isLoadingStripeAccount}
                startIcon={
                  isProcessing || isLoadingStripeAccount ?
                    <CircularProgress size={20} color="inherit" /> :
                    <CreditCardIcon />
                }
                sx={{
                  background: tok('action'),
                  color: tok('onAction'),
                  fontWeight: 700,
                  py: 1.75,
                  fontSize: '1.05rem',
                  borderRadius: '12px',
                  textTransform: 'none',
                  mt: 1,
                  '&:hover': {
                    background: tok('action'),
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(196, 125, 78, 0.45)',
                  },
                  '&:disabled': {
                    background: 'rgba(245, 240, 232, 0.25)',
                    color: 'rgba(245, 240, 232, 0.6)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {isProcessing ? 'Loading...' :
                 isLoadingStripeAccount ? 'Loading Account...' :
                 'Pay Now'}
              </Button>
            )}
          </>
        )}

        {/* Terminal / non-actionable states. Status is server-owned; this only displays it. */}
        {(() => {
          const notice: Record<string, { text: string; good?: boolean }> = {
            [PaymentStatus.PAID]: { text: 'Payment Complete', good: true },
            [PaymentStatus.PARTIALLY_REFUNDED]: { text: 'Paid (partially refunded)', good: true },
            [PaymentStatus.PROCESSING]: { text: 'Processing your payment' },
            [PaymentStatus.REFUNDED]: { text: 'Refunded' },
            [PaymentStatus.CANCELLED]: { text: 'Cancelled by the business' },
            [PaymentStatus.DISPUTED]: { text: 'This payment is under review' },
            [PaymentStatus.DISPUTE_LOST]: { text: 'This payment is under review' },
            [PaymentStatus.UNKNOWN]: { text: 'Status unavailable' },
          };
          const n = notice[status];
          if (!n) return null;
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 1.25,
              backgroundColor: n.good ? 'rgba(127, 168, 145, 0.18)' : 'rgba(245, 240, 232, 0.08)',
              border: `1px solid ${n.good ? 'rgba(127, 168, 145, 0.35)' : 'rgba(245, 240, 232, 0.15)'}`,
              borderRadius: '12px',
            }}>
              {n.good && <CheckCircleIcon sx={{ color: tok('onBusinessBubble'), fontSize: 24, mr: 1 }} />}
              <Typography variant="subtitle1" sx={{ color: tok('onBusinessBubble') }}>
                {n.text}
              </Typography>
            </Box>
          );
        })()}
      </Paper>

      {/* Payment Sheet Dialog */}
      <Dialog
        open={showPaymentSheet}
        onClose={() => !isProcessing && setShowPaymentSheet(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{
          background: tok('businessBubble'),
          color: tok('onBusinessBubble'),
          p: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">
            Complete Payment
          </Typography>
          <IconButton
            onClick={() => !isProcessing && setShowPaymentSheet(false)}
            disabled={isProcessing}
            sx={{ color: tok('onBusinessBubble') }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <DialogContent sx={{ p: 3 }}>
          {paymentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {paymentError}
            </Alert>
          )}

          {clientSecret ? (
            <>
              {isLoadingStripeAccount && (
                <Typography sx={{ mb: 2 }}>Configuring payment...</Typography>
              )}
              {accountStripePromise && (
                <Elements 
                  stripe={accountStripePromise} 
                  options={{ clientSecret: clientSecret }}
                >
              <PaymentForm
                clientSecret={clientSecret}
                amount={paymentData.amount}
                currency={paymentData.currency}
                description={paymentData.description}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                onSuccess={async () => {
                  setShowPaymentSheet(false);
                  setShowSuccess(true);
                  onPaymentComplete?.(message);

                  // 5-second backup poll: if the webhook hasn't landed yet,
                  // ask the server to reconcile with Stripe. The server owns
                  // all Firestore status writes; the usePaymentStatus
                  // listener will reflect the update — the client never
                  // writes paymentData.status itself.
                  setTimeout(async () => {
                    if (actualPaymentIntentId) {
                      try {
                        // /api/payment-status verifies with Stripe and updates
                        // Firestore server-side when the status changed.
                        // (Requires a Firebase ID token; anonymous auth is fine.)
                        const idToken = await AuthService.getInstance().getIdToken();
                        await fetch(`/api/payment-status/${actualPaymentIntentId}`, {
                          headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
                        });
                      } catch (error) {
                        console.error('Failed to check payment status:', error);
                      }
                    }
                  }, 5000);
                }}
                onError={(error) => {
                  setPaymentError(error);
                }}
              />
              </Elements>
              )}
            </>
          ) : (
            <Alert severity="warning">
              💳 Loading payment form...<br/>
              <small>No clientSecret available</small>
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setShowPaymentSheet(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success"
          sx={{ 
            '& .MuiAlert-message': { 
              fontWeight: 'bold' 
            } 
          }}
        >
          Payment successful! 🎉
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentBubble;